<template>
  <div class="page">
    <Modal
      justifyContent="flex-start"
      textAlign="flex-start"
      width="750px"
      :height="isMobile ? '80vh' : 'auto'"
      :show="donorStore.newUser || isTestMode"
      @close="closeModal"
    >
      <template #header>
        <div class="welcome-header">
          <div class="welcome-title" data-test="step-title">
            {{
              currentStep == 1
                ? 'Welcome'
                : currentStep == 2
                ? 'About You'
                : currentStep == 3
                ? 'Preferences'
                : currentStep == 4
                ? 'Review'
                : 'Thank You!'
            }}
          </div>
          <div
            class="current-step"
            v-if="currentStep != 5 && currentStep != 1 && !skipPreferences"
          >
            Step {{ currentStep - 1 + '/' + 3 }}
          </div>
        </div></template
      >

      <div :style="{ width: isMobile ? '300px' : '700px' }">
        <component :is="currentStepComponent" />
      </div>

      <template #footer>
        <Button
          v-if="currentStep !== 5"
          class="p-button-text"
          :class="{ invisible: currentStep === 1 }"
          :style="{ color: donorStore.mainColor }"
          @click="prevStep"
          data-test="back-button"
        >
          Back
        </Button>
        <Button
          v-if="currentStep < 4"
          class="p-button"
          :style="{
            backgroundColor: mainColor,
            borderColor: mainColor,
            borderRadius: '4px',
            color: getContrastYIQ(donorStore.mainColor),
          }"
          @click="nextStep"
          data-test="next-button"
        >
          Next
        </Button>
        <Button
          v-if="currentStep === 4"
          class="p-button"
          :style="{
            backgroundColor: mainColor,
            borderColor: mainColor,
            borderRadius: '4px',
            color: getContrastYIQ(donorStore.mainColor),
          }"
          @click="nextStep"
          data-test="confirm-button"
        >
          Confirm
        </Button>
        <Button
          v-if="currentStep === 5"
          class="p-button"
          :style="{
            backgroundColor: mainColor,
            borderColor: mainColor,
            borderRadius: '4px',
            color: getContrastYIQ(donorStore.mainColor),
          }"
          @click="closeModal"
          data-test="complete-button"
        >
          Complete
        </Button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import Modal from '@/components/Modal.vue';
import Step1 from './welcomeSteps/Step1.vue';
import Step2 from './welcomeSteps/Step2.vue';
import Step3 from './welcomeSteps/Step3.vue';
import Step4 from './welcomeSteps/Step4.vue';
import Step5 from './welcomeSteps/Step5.vue';
import useIsMobile from '@/composables/useIsMobile.js';

const { isMobile } = useIsMobile();

const props = defineProps({
  step: {
    type: Number,
    default: 1,
  },
});

const donorStore = useDonorStore();
const mainColor = donorStore.mainColor;
const donorId = donorStore.donorInfo.donorAccounts_id;
const skipPreferences = ref(false);

onBeforeMount(async () => {
  await donorStore.fetchHostPreferences();

  if (
    donorStore.hostPreferences.categories.length == 0 &&
    donorStore.hostPreferences.orgSizes.length == 0 &&
    donorStore.hostPreferences.areasOfImpact.length == 0
  ) {
    skipPreferences.value = true;
  }
});

const currentStep = ref(props.step);
const currentStepComponent = computed(() => {
  switch (currentStep.value) {
    case 1:
      return Step1;
    case 2:
      return Step2;
    case 3:
      return Step3;
    case 4:
      return Step4;
    case 5:
      return Step5;
    default:
      return Step1;
  }
});

const nextStep = async () => {
  if (currentStep.value === 2 && !validateStep2()) {
    return;
  }

  if (currentStep.value === 4) {
    updateDonorInfo();
  }

  if (currentStep.value === 5) {
    closeModal();
    return;
  }
  if (currentStep.value < 5) {
    currentStep.value++;
  }
  if (skipPreferences.value && currentStep.value === 3) {
    currentStep.value++;
  }
};
const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
  if (skipPreferences.value && currentStep.value === 3) {
    currentStep.value--;
  }
};

const closeModal = () => {
  donorStore.errors = {};
  donorStore.newUser = false;
  currentStep.value = 1;
  localStorage.setItem('seenIntro', 'true');
};

const validateStep2 = () => {
  donorStore.errors = {}; // Clear previous errors
  if (!donorStore.donorInfo.firstName) {
    donorStore.errors.firstName = 'First Name is required.';
  }
  if (!donorStore.donorInfo.lastName) {
    donorStore.errors.lastName = 'Last Name is required.';
  }
  return Object.keys(donorStore.errors).length === 0;
};

const updateDonorInfo = () => {
  donorStore.updateDonorAccount(donorId, {
    prefix: donorStore.donorInfo.prefix,
    firstName: donorStore.donorInfo.firstName,
    middleName: donorStore.donorInfo.middleName,
    lastName: donorStore.donorInfo.lastName,
    suffix: donorStore.donorInfo.suffix,
    pronouns: donorStore.donorInfo.pronouns,
    email: donorStore.donorInfo.email,
    homePhoneNumber: donorStore.donorInfo.homePhoneNumber,
    addressLine1: donorStore.donorInfo.addressLine1,
    addressLine2: donorStore.donorInfo.addressLine2,
    city: donorStore.donorInfo.city,
    state: donorStore.donorInfo.state,
    stateOther: donorStore.donorInfo.stateOther,
    zip: donorStore.donorInfo.zip,
    country: donorStore.donorInfo.country,
    preferences: {
      categories: donorStore.donorPreferences.categories.selected,
      orgSizes: donorStore.donorPreferences.orgSizes.selected,
      areasOfImpact: donorStore.donorPreferences.areasOfImpact.selected,
    },
  });
  donorStore.donorPreferences.categories = donorStore.donorPreferences.categories.selected;
  donorStore.donorPreferences.orgSizes = donorStore.donorPreferences.orgSizes.selected;
  donorStore.donorPreferences.areasOfImpact = donorStore.donorPreferences.areasOfImpact.selected;
};

const urlParams = new URLSearchParams(window.location.search);
const isTestMode = urlParams.get('testMode') === 'true';

</script>
<style lang="scss">
.welcome-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  top: 24px;
  left: 48px;
  width: 600px;
}

.welcome-title {
  font-size: 2.75rem;
  font-weight: bold;
  color: var(--p-primary-500);
}

.current-step {
  font-size: 1rem;
  font-weight: 400;
}

.scrollbar::-webkit-scrollbar {
  width: 10px;
  height: 20px;
}

.scrollbar::-webkit-scrollbar-track {
  background: #fff;
}

.scrollbar::-webkit-scrollbar-thumb {
  background: var(--p-primary-500);
  border: 3px solid #fff;
}

.scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--p-primary-500);
}

@media screen and (max-width: 768px) {
  .welcome-header {
    flex-direction: column;
    position: static;
  }
  .welcome-title {
    font-size: 1.875rem;
  }
}
</style>
